import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import axios from "axios";
import Select from "react-select";
import { useAuthToken } from "../TokenContext";
import { useIntl } from "react-intl";
import PersonIcon from "@mui/icons-material/Person";
import moment from "moment";
import {
  Drawer, TextField, Button, CircularProgress, alpha, useTheme,
  styled,
  Paper,
  Typography,
  Box,
  Alert,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  Checkbox,
  Chip,
  IconButton,
  Tooltip
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { useSelector } from "react-redux";
import "./kanband.css";

import { FaBug, FaJira } from 'react-icons/fa';
// Styled Components
// Styled Components
const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})(({ theme, collapsed }) => ({
  position: 'relative',
  width: '100%',
  marginLeft: 0,
  overflowX: 'hidden',
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
    width: '100%'
  },
  backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : '#F5F5F5',
  color: theme.palette.text.primary,
  minHeight: 'calc(100vh - 64px)',
  padding: theme.spacing(3)
}));
const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#fff',
    color: theme.palette.text.primary,
  }
}));
const FiltersContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    '& > *': {
      width: '100%'
    }
  }
}));

const SelectWrapper = styled(Box)(({ theme }) => ({
  width: "20rem",
  marginBottom: theme.spacing(4),
  '& .select__control': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
    borderColor: theme.palette.divider,
    minHeight: 56,
    // marginTop: 10,
    '&:hover': {
      borderColor: theme.palette.primary.main,
    }
  },
  '& .select__menu': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
    color: theme.palette.text.primary,
    zIndex: 999,
    boxShadow: theme.shadows[3]
  },
  '& .select__option': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
    color: theme.palette.text.primary,
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1)
    }
  }
}));

const SprintSelect = styled('select')(({ theme }) => ({
  textDecoration: 'none',
  border: '1px solid rgb(233, 224, 224)',
  borderRadius: '4px',
  width: "20rem",
  height: '56px',
  padding: '0 0.5rem',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
  color: theme.palette.text.primary,
  cursor: 'pointer',
  '&:focus': {
    borderColor: theme.palette.primary.main,
    outline: 'none',
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
  }
}));



const StyledColumn = styled(Box)(({ theme, collapsed }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  minWidth: '280px',
  width: '280px',
  marginRight: theme.spacing(2),
  maxHeight: 'calc(100vh - 220px)', // Account for header, controls and padding
  overflowY: 'auto',
  scrollbarWidth: 'none',  // Firefox
  msOverflowStyle: 'none',  // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none'  // Chrome, Safari, Opera
  },
  boxShadow: theme.shadows[1],
  '& .column-header': {
    borderBottom: '3px solid',
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }
}));

const StyledTask = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#fff',
  border: `1px solid ${theme.palette.divider}`,
  borderLeft: `6px solid ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[2]
  },
  '& .task-content': {
    width: '100%',
    textAlign: 'left'
  },
  '& .task-header': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  '& .task-title': {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.primary
  },
  '& .task-id': {
    fontSize: '0.75rem',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: '2px 8px',
    borderRadius: '4px',
    display: 'inline-block',
    marginBottom: theme.spacing(1)
  },
  '& .task-assignee': {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary
  },
  '& .task-due-date': {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  }
}));

const ScrollButton = styled(Button)(({ theme }) => ({
  position: 'fixed',
  top: '50%',
  transform: 'translateY(-50%)',
  width: 40,
  height: 40,
  minWidth: 40,
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '50%',
  boxShadow: theme.shadows[2],
  zIndex: 1,
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}));

const ActionBar = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
  borderTop: `2px solid ${theme.palette.primary.main}`,
  boxShadow: theme.shadows[8],
  zIndex: 1100,
}));


const KanbanboardTalent = ({ collapsed }) => {
  // State declarations
  const [tasks, setTasks] = useState([]);
  const [columns, setColumns] = useState({
    "To Do": { title: "To Do", items: [], type: "open" },
    "In Progress": { title: "In Progress", items: [], type: "open" },
    Done: { title: "Done", items: [], type: "closed" },
  });
  const [selectedPodId, setSelectedPodId] = useState([]);
  const [authorizationDetails, setAuthorizationDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState([]);
  const [podNames, setPodNames] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [isLaunchingPipeline, setIsLaunchingPipeline] = useState(false);
  const [showPipelineResult, setShowPipelineResult] = useState(false);
  const [pipelineResult, setPipelineResult] = useState(null);
  const [isMockMode, setIsMockMode] = useState(false);
  const theme = useTheme();
  const [sprintData, setSprintData] = useState([]);
  const [selectedSprint, setSelectedSprint] = useState({
    name: "", // Selected sprint name
    startDate: "", // Selected sprint start date
    endDate: "", // Selected sprint end date
  });
  const token = useAuthToken();
  const intl = useIntl();
  const { user } = useSelector((state) => state.auth);
  const scrollContainerRef = useRef(null);

  // Initial data fetching
  useEffect(() => {
    fetchPodNames();
  }, [token]);

  useEffect(() => {
    if (selectedPodId) {
      fetchAuthorizationTokensAndList();
    } else {
      resetStates();
    }
  }, [selectedPodId, selectedCandidate, selectedSprint.name]);

  const resetStates = () => {
    setCandidates([]);
    setTasks([]);
    setSelectedTasks([]);
    setColumns({
      "To Do": { title: "To Do", items: [], type: "open" },
      "In Progress": { title: "In Progress", items: [], type: "open" },
      Done: { title: "Done", items: [], type: "closed" },
    });
  };

  // Helper functions
  /* istanbul ignore next */
  const parseStatusString = (statusString) => {
    if (!statusString) return [];
    return statusString.split(",").map((status) => status.trim());
  };

  const handleLoadMockData = () => {
    setIsMockMode(true);
    const mockTasks = [
      {
        pod_task_id: 'mock-1',
        pod_task_name: 'Implement User Login',
        status: 'To Do',
        Candidate_Name: 'John Doe',
        due_date: new Date().toISOString()
      },
      {
        pod_task_id: 'mock-2',
        pod_task_name: 'Fix Dashboard Bug',
        status: 'In Progress',
        Candidate_Name: 'Jane Smith',
        due_date: new Date().toISOString()
      },
      {
        pod_task_id: 'mock-3',
        pod_task_name: 'Add Profile Picture Upload',
        status: 'To Do',
        Candidate_Name: 'Alice Johnson',
        due_date: new Date().toISOString()
      }
    ];

    setTasks(mockTasks);

    // Ensure columns are properly initialized for mock tasks
    const newColumns = {
      "To Do": { title: "To Do", items: mockTasks.filter(t => t.status === "To Do"), type: "open" },
      "In Progress": { title: "In Progress", items: mockTasks.filter(t => t.status === "In Progress"), type: "open" },
      "Done": { title: "Done", items: [], type: "closed" },
    };
    setColumns(newColumns);
  };

  const handleLaunchAgentPipeline = async (overrideTasks = null) => {
    const taskIdsToUse = overrideTasks || selectedTasks;

    if (taskIdsToUse.length === 0) {
      alert('Please select at least one task first.');
      return;
    }

    setIsLaunchingPipeline(true);
    const selectedTaskDetails = tasks.filter(task =>
      taskIdsToUse.includes(task.key || task.pod_task_id)
    );

    if (isMockMode) {
      // Simulate API call for 2 seconds
      setTimeout(() => {
        setPipelineResult({ success: true, data: { message: "Mock Success" }, count: selectedTaskDetails.length });
        setSelectedTasks([]);
        setIsLaunchingPipeline(false);
        setShowPipelineResult(true);
      }, 2000);
      return;
    }

    const featureRequests = selectedTaskDetails.map(task => ({
      title: task.summary || task.pod_task_name || `Task ${task.key || task.pod_task_id}`,
      description: task.description || task.summary || task.pod_task_name || '',
      priority: task.priority || 'Medium',
      task_id: task.key || task.pod_task_id,
      source: task.key ? 'jira' : 'pod_task',
    }));

    const featureText = selectedTaskDetails.map(task =>
      `Task: ${task.summary || task.pod_task_name || `Task ${task.key || task.pod_task_id}`}\nDescription: ${task.description || task.summary || task.pod_task_name || ''}`
    ).join("\n\n");

    const payload = {
      feature_text: featureText,
      manager_id: user?.data[0]?.emp_id || user?.data[0]?.c_id || 'unknown_manager',
      org_code: user?.data[0]?.talent_code || user?.data[0]?.org_code || 'G10',
      pod_id: selectedPodId && selectedPodId.length > 0 ? selectedPodId[0] : 1046,
      // Sending existing fields just in case some other backend logic depends on them
      feature_requests: featureRequests,
      client_id: user?.data[0]?.emp_id,
      client_name: user?.data[0]?.name || user?.data[0]?.email || '',
      source: 'kanban_board',
      deploy_app: true,
      auto_approve: false,
    };

    try {
      const response = await axios.post(
        'https://agentsdev.artemy.ai/feature-submission',
        payload,
        {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          },
        }
      );
      setPipelineResult({ success: true, data: response.data, count: selectedTaskDetails.length });
      setSelectedTasks([]);
    } catch (err) {
      console.error('Launch Agent Pipeline failed:', err);
      setPipelineResult({ success: false, error: err?.response?.data?.message || err.message, count: selectedTaskDetails.length });
    } finally {
      setIsLaunchingPipeline(false);
      setShowPipelineResult(true);
    }
  };

  const handleTaskSelection = (taskId, event) => {
    event.stopPropagation();
    setSelectedTasks(prev => {
      if (prev.includes(taskId)) {
        return prev.filter(id => id !== taskId);
      } else {
        return [...prev, taskId];
      }
    });
  };

  const handleClearSelection = () => {
    setSelectedTasks([]);
  };

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const currentScroll = scrollContainerRef.current.scrollLeft;
      scrollContainerRef.current.scrollTo({
        left: currentScroll + (direction === "left" ? -scrollAmount : scrollAmount),
        behavior: "smooth",
      });
    }
  };

  // Column initialization
  const initializeColumns = (authData) => {
    if (!authData) return columns;

    const openStatuses = parseStatusString(authData.jira_open_story_bug_status?.[0]);
    const closedStatuses = parseStatusString(authData.jira_closed_story_bug_status?.[0]);

    const newColumns = {};
    openStatuses.forEach((status) => {
      newColumns[status] = { title: status, items: [], type: "open" };
    });
    closedStatuses.forEach((status) => {
      newColumns[status] = { title: status, items: [], type: "closed" };
    });

    return newColumns;
  };

  // Task grouping
  const groupTasksByStatus = (tasks, columns) => {
    const groupedColumns = { ...columns };
    Object.keys(groupedColumns).forEach((key) => {
      groupedColumns[key].items = [];
    });

    tasks.forEach((task) => {
      const status = task.status || "To Do";
      if (groupedColumns[status]) {
        groupedColumns[status].items.push(task);
      }
    });

    return groupedColumns;
  };

  // Comment rendering and handling functions
  const renderJiraComment = (content) => {
    if (!content) return null;

    return content.map((item, index) => {
      if (item.type === 'paragraph') {
        return (
          <div key={index} style={{ marginBottom: '8px' }}>
            {item.content?.map((contentItem, cIndex) => {
              if (contentItem.type === 'text') {
                return <span key={cIndex}>{contentItem.text}</span>;
              }
              if (contentItem.type === 'mention') {
                return (
                  <span
                    key={cIndex}
                    style={{
                      color: '#0052CC',
                      fontWeight: '500',
                      backgroundColor: '#EAE6FF',
                      padding: '0 4px',
                      borderRadius: '3px',
                      margin: '0 2px'
                    }}
                  >
                    {contentItem.attrs.text}
                  </span>
                );
              }
              return contentItem.type === 'hardBreak' ? <br key={cIndex} /> : null;
            })}
          </div>
        );
      }
      return null;
    });
  };
  const renderComments = () => {
    if (!selectedTask) return null;



    // Handle POD task comments

    if (selectedTask.pod_task_id && selectedTask.comment) {
      try {
        const podComments = JSON.parse(selectedTask.comment);
        return (
          <>

            {podComments.map((comment, index) => (
              <div
                key={index}
                className="comment"
                style={{
                  marginBottom: "20px",
                  padding: "12px",
                  backgroundColor: theme => theme.palette.mode === 'dark'
                    ? theme.palette.background.paper
                    : '#f8f9fa',
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                  alignItems: "center",
                }}>

                  <span style={{
                    backgroundColor: "#0073e6",
                    color: "white",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}>
                    {comment.author}
                  </span>
                  <small style={{ color: "#666" }}>
                    {moment(comment.timestamp).format("MMM DD, YYYY HH:mm")}
                  </small>
                </div>
                <div style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  lineHeight: "1.5",
                }}>
                  {comment.text}
                </div>
              </div>
            ))}
          </>
        );
        /* istanbul ignore next */
      } catch (error) {
        console.error("Error parsing POD comments:", error);
        return null;
      }
    }

    // Handle Jira comments
    return (
      <>
        <Box><Typography variant="subtitle1">
          Comments
        </Typography></Box>

        {selectedTask.comments?.map((comment, index) => (
          <div
            key={index}
            className="comment"
            style={{
              marginBottom: "20px",
              padding: "12px",
              backgroundColor: theme => theme.palette.mode === 'dark'
                ? theme.palette.background.paper
                : '#f8f9fa',
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >

            <div style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "12px",
              alignItems: "center",
            }}>
              <span style={{
                backgroundColor: "#0073e6",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "14px",
                fontWeight: "500",
              }}>
                {comment.author}
              </span>
              <small style={{ color: "#666" }}>
                {moment(comment.created).format("MMM DD, YYYY HH:mm")}
              </small>
            </div>
            <div style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: "1.5",
            }}>
              {renderJiraComment(comment.body?.content)}
            </div>
          </div>
        ))}
      </>
    );
  }

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTask) return;

    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };

    try {
      if (selectedTask.pod_task_id) {
        // For POD tasks, prepare array of all comments including the new one
        let existingComments = [];
        if (selectedTask.comment) {
          try {
            existingComments = JSON.parse(selectedTask.comment);
            /* istanbul ignore next */
          } catch (error) {
            console.error("Error parsing existing comments:", error);
            existingComments = [];
          }
        }

        // Create the new comment object
        const newCommentObj = {
          pod_task_id: parseInt(selectedTask.pod_task_id),
          user_id: parseInt(user.data[0].c_id),
          new_text: newComment,
          is_reply: false,
          author: user.data[0].firstName
        };

        // Create array of all comments (previous + new)
        const allComments = existingComments.map(comment => ({
          pod_task_id: parseInt(selectedTask.pod_task_id),
          user_id: parseInt(comment.user_id),
          new_text: comment.text,
          is_reply: false
        }));
        allComments.push(newCommentObj);

        // Send request with all comments
        const response = await axios.post(
          process.env.REACT_APP_CLIENT_ADDCOMENTPODTASK,
          [newCommentObj],
          { headers }
        );

        if (response.status === 200) {
          setSelectedTask(prevTask => ({
            ...prevTask,
            comment: JSON.stringify([
              ...existingComments,
              {
                text: newComment,
                user_id: parseInt(user.data[0].c_id),
                timestamp: new Date().toISOString(),
                replies: []
              }
            ])
          }));
          setNewComment("");
        }
      } else if (selectedTask.key) {
        // Jira comment handling remains the same
        const response = await axios.post(
          process.env.REACT_APP_CLIENT_ADDCOMMENTJIRAPODBOARD,
          {
            jiraUrl: authorizationDetails.Jira_URL,
            issueKey: selectedTask.key,
            commentText: newComment,
            username: authorizationDetails.Jira_admin_login_email_address,
            password: authorizationDetails.Jira_admin_API_Token,
          },
          { headers }
        );

        if (response.status === 200) {
          setSelectedTask((prevTask) => ({
            ...prevTask,
            comments: [
              ...(prevTask.comments || []),
              {
                author: user.data[0].firstName || "current_user",
                body: {
                  type: "doc",
                  version: 1,
                  content: [
                    {
                      type: "paragraph",
                      content: [{ type: "text", text: newComment }],
                    },
                  ],
                },
                created: new Date().toISOString(),
              },
            ],
          }));
          setNewComment("");
        }
      }
      /* istanbul ignore next */
    } catch (error) {
      console.error("Error adding comment:", error);
      alert("Failed to add comment. Please try again.");
    }
  };

  // Data fetching functions

  const fetchPodNames = async () => {
    try {
      const response = await axios.post(
        process.env.REACT_APP_TALENT_GETPODSFORTALENT,
        { c_id: user.data[0].c_id },
        { headers: { Authorization: token, "Content-Type": "application/json" } }
      );
      const podNameOptions = response.data.body.map(pod => ({
        label: pod.pod_name,
        value: pod.pod_id,
      }));
      setPodNames(podNameOptions);
      /* istanbul ignore next */
    } catch (error) {
      console.error("Error fetching pod names:", error);
    }
  };

  useEffect(() => {
    fetchSprintfilter();
  }, [token]);
  const fetchSprintfilter = async () => {
    if (!token) return;

    try {
      const response = await fetch(process.env.REACT_APP_TALENT_SPRINTFILTERTAENT, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ talent_id: user.data[0].c_id }),
      });
      const data = await response.json();
      if (data.statusCode === 200 && data.body?.sprint_filter) {
        setSprintData(data.body.sprint_filter);
      }
      /* istanbul ignore next */
    } catch (error) {
      console.error('Error fetching sprint data:', error);
    }
  };
  const handleSprintSelect = (e) => {
    const selectedSprintName = e.target.value;
    const selectedSprintData = sprintData.find(
      (sprint) => sprint.sprint_name === selectedSprintName
    );
    setSelectedSprint({
      name: selectedSprintName,
      startDate: selectedSprintData ? selectedSprintData.sprint_start_date : "",
      endDate: selectedSprintData ? selectedSprintData.sprint_end_date : "",
    });
  };


  const fetchAuthorizationTokensAndList = async () => {
    setLoading(true);

    if (!selectedPodId || selectedPodId.length === 0) {
      setLoading(false);
      return;
    }

    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };

    try {
      // Fetch authorization tokens
      const authorizationResponse = await axios.post(
        process.env.REACT_APP_CUSTOMERADMIN_FETCHAUTHORIZATIONTOKENS,
        {
          Organizational_Code: user.data[0].talent_code
        },
        { headers }
      );

      const authorizationData = authorizationResponse.data.body.data[0];
      setAuthorizationDetails(authorizationData);

      // Initialize columns
      const initialColumns = initializeColumns(authorizationData);
      setColumns(initialColumns);

      // Fetch candidates if pod is selected
      if (selectedPodId.length > 0) {
        const emailFetchingResponse = await axios.post(
          process.env.REACT_APP_CLIENT_FETCHPODCANDIDATES,
          { podID: selectedPodId },
          { headers }
        );

        const allCandidatesData = emailFetchingResponse?.data?.data;
        const allCandidates = Array.isArray(allCandidatesData) ? allCandidatesData : [];
        setCandidates(allCandidates);

        // Get selected candidate names
        const candidateNames = selectedCandidate.map(option => option.value);

        // Bypassing sprint selection check temporarily for testing
        // if (selectedSprint.name) {
        const listPayload = {
          pod_Id: [1046],
          username: "contact@brilliglobal.com",
          password: "ATATT3xFfGF0UTaP_R05Cig8NOzL1wx21mQXoSkLoXoRI1Bvx4pYcsUfTAR2rxhixtqy3FQuPd61l9PFnT53XfK2uItJvOdkOlp54VoOO0RYk7Aj057es7hXeXW5zH0oKgCAsse56T-hzc6790ydxCNDI1H_o7IsZt-JWpyv7HbkkMx2143Rwrc=89A7304D",
          projectKey: "G10",
          jira_url: "https://globl.atlassian.net",
          assignees: [
            "tulasibavisetti",
            "Akhil K A"
          ],
          candidateName: [],
          sprintName: "G10 Sprint 18"
        };

        // Fetch tasks with all filters applied
        // const response = await axios.post(
        //   process.env.REACT_APP_CLIENT_TESTSINGLEVIEWTASK,
        //   listPayload,
        //   { headers }
        // );

        const rawMockResponse = {
          "statusCode": 200,
          "body": "{\"status\":200,\"data\":{\"podMembers\":[],\"jiraIssues\":[{\"key\":\"G10-264\",\"summary\":\"Enhance Talent Dashboard to Display JIRA Stories and Defects\",\"status\":\"Done-PROD\",\"Candidate_Name\":\"Unassigned\",\"assigneeEmail\":\"\",\"assigneeAccountId\":\"\",\"sprint\":\"G10 Sprint 18\",\"comments\":[]},{\"key\":\"G10-262\",\"summary\":\"Sprint Summary/Retro summary\",\"status\":\"To Do\",\"Candidate_Name\":\"Unassigned\",\"assigneeEmail\":\"\",\"assigneeAccountId\":\"\",\"sprint\":\"G10 Sprint 18\",\"comments\":[]},{\"key\":\"G10-200\",\"summary\":\"Sprint plan- Projected pod velocity\",\"status\":\"To Do\",\"Candidate_Name\":\"Unassigned\",\"assigneeEmail\":\"\",\"assigneeAccountId\":\"\",\"sprint\":\"G10 Sprint 18\",\"comments\":[]},{\"key\":\"G10-199\",\"summary\":\"Generate Sprint Plan\",\"status\":\"To Do\",\"Candidate_Name\":\"Unassigned\",\"assigneeEmail\":\"\",\"assigneeAccountId\":\"\",\"sprint\":\"G10 Sprint 18\",\"comments\":[{\"author\":\"tulasibavisetti\",\"body\":{\"type\":\"doc\",\"version\":1,\"content\":[{\"type\":\"paragraph\",\"content\":[{\"type\":\"text\",\"text\":\"comments\"}]}]},\"created\":\"2024-11-04T04:59:30.617-0500\"}]},{\"key\":\"G10-185\",\"summary\":\"Talent Dashboard- Tasks & Assigned Items \",\"status\":\"Redo-Prod\",\"Candidate_Name\":\"Akhil K A\",\"assigneeEmail\":\"\",\"assigneeAccountId\":\"60ee50ad413fc70069ed187d\",\"sprint\":\"G10 Sprint 18\",\"comments\":[{\"author\":\"Brilli Global, Inc\",\"body\":{\"type\":\"doc\",\"version\":1,\"content\":[{\"type\":\"paragraph\",\"content\":[{\"type\":\"mention\",\"attrs\":{\"id\":\"6273a85c106b60006f5906f4\",\"text\":\"@tulasibavisetti\",\"accessLevel\":\"\"}},{\"type\":\"text\",\"text\":\" \"},{\"type\":\"mention\",\"attrs\":{\"id\":\"6406dedc2847866310007da1\",\"text\":\"@Raghuveer Talari\",\"accessLevel\":\"\"}},{\"type\":\"text\",\"text\":\" - FYI- For UI updates corresponding to this. \"}]}]},\"created\":\"2024-02-26T01:42:44.905-0500\"}]},{\"key\":\"G10-70\",\"summary\":\"Globl-AI Assistant\",\"status\":\"In Progress\",\"Candidate_Name\":\"Akhil K A\",\"assigneeEmail\":\"\",\"assigneeAccountId\":\"60ee50ad413fc70069ed187d\",\"sprint\":\"G10 Sprint 18\",\"comments\":[]},{\"key\":\"G10-3\",\"summary\":\"POD Retros\",\"status\":\"Done-PROD\",\"Candidate_Name\":\"Prithvi Kolachena\",\"assigneeEmail\":\"\",\"assigneeAccountId\":\"610bc2eae6e6f80071181f66\",\"sprint\":\"G10 Sprint 18\",\"comments\":[]}]}}"
        };

        const response = {
          data: JSON.parse(rawMockResponse.body)
        };

        // Combine and process tasks
        const combinedTasks = [
          ...(response.data.data.podMembers || []).map((task) => ({
            ...task,
            comments: task.comments || [],
          })),
          ...(response.data.data.jiraIssues || []).map((task) => ({
            ...task,
            comments: task.comments || [],
          })),
        ];

        setTasks(combinedTasks);
        setColumns(groupTasksByStatus(combinedTasks, initialColumns));
        // } else {
        //   setTasks([]);
        // }
      }
      /* istanbul ignore next */
    } catch (error) {
      console.error("Error in fetchAuthorizationTokensAndList:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskClick = async (task) => {
    const fullTask = tasks.find(
      (t) => (t.key || t.pod_task_id) === (task.key || task.pod_task_id)
    );

    if (fullTask) {
      setSelectedTask(fullTask);
      if (fullTask.pod_task_id) {
        try {
          const headers = {
            Authorization: token,
            "Content-Type": "application/json",
          };

          const response = await axios.post(
            process.env.REACT_APP_CLIENT_FETCHPODTASKCOMMENT,
            { pod_task_id: parseInt(fullTask.pod_task_id) },
            { headers }
          );

          // if (response.status === 200) {
          //   setSelectedTask(prevTask => ({
          //     ...prevTask,
          //     comment: response.data.comment
          //   }));
          // }
          /* istanbul ignore next */
        } catch (error) {
          console.error("Error fetching pod task comments:", error);
        }
      }
    } else {
      setSelectedTask({
        ...task,
        comments: [],
      });
    }
  };

  const handleDrawerClose = () => {
    setSelectedTask(null);
    setNewComment("");
  };

  // Drag and drop handlers
  const onDragEnd = async (result) => {
    if (!result.destination || isUpdating) return;

    const { source, destination, draggableId } = result;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const task = Object.values(columns).reduce((found, column) => {
      if (found) return found;
      return column.items.find((task, index) => {
        const taskId = task.key || task.pod_task_id || `task-${index}`;
        return taskId.toString() === draggableId;
      });
    }, null);

    if (!task) return;

    const oldColumns = columns;
    const newColumns = { ...columns };
    const sourceColumn = columns[source.droppableId];
    const destColumn = columns[destination.droppableId];

    /* istanbul ignore next */
    if (!sourceColumn || !destColumn) return;

    /* istanbul ignore next */
    const sourceItems = [...sourceColumn.items];
    /* istanbul ignore next */
    const destItems = [...destColumn.items];

    const [removed] = sourceItems.splice(source.index, 1);
    const movedTask = { ...removed, status: destination.droppableId };
    destItems.splice(destination.index, 0, movedTask);

    newColumns[source.droppableId] = {
      ...sourceColumn,
      items: sourceItems,
    };
    newColumns[destination.droppableId] = {
      ...destColumn,
      items: destItems,
    };

    setColumns(newColumns);
    setIsUpdating(true);

    try {
      const headers = {
        Authorization: token,
        "Content-Type": "application/json",
      };

      if (!authorizationDetails?.Jira_admin_API_Token ||
        !authorizationDetails?.Jira_admin_login_email_address ||
        !authorizationDetails?.Jira_URL) {
        throw new Error("Missing authorization details");
      }

      if (task.pod_task_id) {
        // Update pod task
        const updateResponse = await axios.put(
          process.env.REACT_APP_CLIENT_UPDATEKANBANBOARDPODTASK,
          {
            pod_task_id: task.pod_task_id,
            status: destination.droppableId,
          },
          { headers }
        );

        if (updateResponse.status !== 200) {
          throw new Error("Failed to update pod task status");
        }
      } else {
        // Update JIRA task
        const transitionId = await fetchTransitionId(
          destination.droppableId,
          task.key
        );

        if (!transitionId) {
          throw new Error("Invalid transition for this status change");
        }

        const updateResponse = await axios.put(
          process.env.REACT_APP_CLIENT_KANBANBOARDUPDATE,
          {
            Jira_URL: authorizationDetails.Jira_URL,
            Jira_admin_login_email_address: authorizationDetails.Jira_admin_login_email_address,
            Jira_admin_API_Token: authorizationDetails.Jira_admin_API_Token,
            key: task.key,
            transitionId: transitionId,
            status: destination.droppableId,
          },
          {
            headers,
            timeout: 10000
          }
        );

        if (updateResponse.status !== 200) {
          throw new Error("Failed to update JIRA task status");
        }
      }
      /* istanbul ignore next */
    } catch (error) {
      console.error("Error updating task status:", error);
      setColumns(oldColumns);
      alert("Failed to update task status. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const fetchTransitionId = async (status, key) => {
    if (!status || !key) return null;

    const headers = {
      Authorization: token,
      "Content-Type": "application/json",
    };

    try {
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_FETCHSTATUSFORSPECIFICJIRAISSUE,
        {
          Jira_admin_login_email_address: authorizationDetails.Jira_admin_login_email_address,
          Jira_admin_API_Token: authorizationDetails.Jira_admin_API_Token,
          key: key,
          Jira_URL: authorizationDetails.Jira_URL,
          status: status,
        },
        {
          headers,
          timeout: 5000
        }
      );

      if (!response.data?.body) {
        throw new Error("Invalid response format");
      }

      const statusData = response.data.body;
      const matchingStatus = statusData.find((s) => s.name === status);

      return matchingStatus?.id || null;
      /* istanbul ignore next */
    } catch (error) {
      console.error("Error fetching transitionId:", error);
      return null;
    }
  };
  // Return/Render section
  return (
    <Box
      sx={{
        bgcolor: theme => theme.palette.mode === 'dark' ? 'background.default' : '#F5F5F5',
        color: 'text.primary',

      }}
    >
      <Typography variant="h4" sx={{ textAlign: 'center', mb: 3 }}>
        {intl.formatMessage({ id: "podBoard" })}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Button variant="outlined" color="secondary" onClick={handleLoadMockData}>
          Load Mock Data (Testing)
        </Button>
      </Box>

      {/* Pod and Candidate Selection Controls */}
      <Box className="board-controls-wrapper">
        <FiltersContainer>
          <SelectWrapper>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{intl.formatMessage({ id: "podSelection" })} *</Typography>
            <Select
              isMulti
              menuPortalTarget={document.body}
              menuPosition="fixed"
              options={podNames}
              value={podNames.filter((option) => selectedPodId.includes(option.value))}
              onChange={(selectedOptions) => setSelectedPodId(selectedOptions.map((opt) => opt.value))}
              placeholder={intl.formatMessage({ id: "selectPodsRequired" })}
              classNamePrefix="select"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 })
              }}
            />
            {!selectedPodId?.length && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {intl.formatMessage({ id: "selectPodToView" })}
              </Typography>
            )}
          </SelectWrapper>

          {selectedPodId?.length > 0 && (
            <>
              <SelectWrapper sx={{ opacity: 1, transition: 'opacity 0.3s' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{intl.formatMessage({ id: "selectSprint" })} *</Typography>
                <SprintSelect
                  data-testid="sprint-select"
                  value={selectedSprint.name}
                  onChange={(e) => {
                    const selectedSprintName = e.target.value;
                    const selectedSprintData = sprintData.find(
                      (sprint) => sprint.sprint_name === selectedSprintName
                    );
                    setSelectedSprint({
                      name: selectedSprintName,
                      startDate: selectedSprintData ? selectedSprintData.sprint_start_date : "",
                      endDate: selectedSprintData ? selectedSprintData.sprint_end_date : "",
                    });
                  }}
                >
                  <option value="">{intl.formatMessage({ id: "selectSprint" })}</option>
                  {sprintData.map((sprint, index) => (
                    <option key={index} value={sprint.sprint_name}>
                      {sprint.sprint_name}
                    </option>
                  ))}
                </SprintSelect>
              </SelectWrapper>

              <SelectWrapper sx={{ opacity: 1, transition: 'opacity 0.3s' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{intl.formatMessage({ id: "talentSelection" })}</Typography>
                <Select
                  isMulti
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  options={Array.isArray(candidates) ? candidates.map(candidate => ({
                    value: candidate.Candidate_Name,
                    label: candidate.Candidate_Name
                  })) : []}
                  value={selectedCandidate}
                  onChange={(selectedOptions) => setSelectedCandidate(selectedOptions || [])}
                  placeholder="Select Candidates"
                  classNamePrefix="select"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 })
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {intl.formatMessage({ id: "filterBySpecificCandidates" })}
                </Typography>
              </SelectWrapper>
            </>
          )}


        </FiltersContainer>

      </Box>

      {/* Kanban Board */}
      <StyledBox collapse={collapsed} >
        {selectedPodId?.length > 0 && !selectedSprint.name && (
          <Alert severity="warning" sx={{ mb: 3, mx: 'auto', maxWidth: '800px', borderRadius: 2 }}>
            Please select a sprint from the dropdown above to view the tasks for the selected pod.
          </Alert>
        )}
        <ScrollButton
          className="scroll-button scroll-left"
          onClick={() => handleScroll("left")}
        >
          ←
        </ScrollButton>

        <DragDropContext onDragEnd={onDragEnd}

        >
          <Box className="scrollable-board" ref={scrollContainerRef}
          >
            {Object.entries(columns).map(([columnId, column]) => (
              <Droppable key={columnId} droppableId={columnId}>
                {(provided, snapshot) => {
                  const columnSelectedCount = column.items.filter(item => 
                    selectedTasks.includes(item.key || item.pod_task_id)
                  ).length;

                  return (
                    <StyledColumn collapsed={collapsed}
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        bgcolor: columnSelectedCount > 0 ? 'rgba(0, 194, 255, 0.05)' : 'background.paper',
                        borderColor: columnSelectedCount > 0 ? 'primary.main' : 'divider',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Box sx={{ 
                        p: 2, 
                        borderBottom: 3,
                        borderColor: column.type === "closed" ? "success.main" : (columnSelectedCount > 0 ? "primary.main" : "warning.main"),
                        bgcolor: 'background.paper'
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {column.title} ({column.items.length})
                        </Typography>
                      </Box>
                      <Box 
                        sx={{ 
                          px: 2, 
                          py: 1.5, 
                          bgcolor: columnSelectedCount > 0 ? 'rgba(0, 194, 255, 0.15)' : 'rgba(0, 0, 0, 0.05)', 
                          borderBottom: '1px solid',
                          borderLeft: `4px solid ${columnSelectedCount > 0 ? 'primary.main' : 'rgba(255, 255, 255, 0.1)'}`,
                          borderColor: 'divider',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'all 0.3s ease',
                          mb: 0.5
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SmartToyIcon sx={{ fontSize: 14, color: columnSelectedCount > 0 ? 'primary.main' : 'text.secondary' }} />
                            <Typography variant="caption" sx={{ 
                              color: columnSelectedCount > 0 ? 'primary.main' : 'text.primary', 
                              fontWeight: 800, 
                              fontSize: '0.7rem', 
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              animation: columnSelectedCount > 0 ? 'pulse 2s infinite' : 'none'
                            }}>
                              Agentic Pipeline
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ 
                            color: 'text.secondary', 
                            fontSize: '0.65rem', 
                            fontWeight: 500,
                            mt: 0.5
                          }}>
                            {columnSelectedCount > 0 ? `${columnSelectedCount} tasks ready to launch` : 'Select tasks below to build'}
                          </Typography>
                        </Box>
                        
                        {columnSelectedCount > 0 && (
                          <Button 
                            size="small" 
                            variant="contained" 
                            startIcon={<PlayArrowIcon sx={{ fontSize: '14px !important' }} />}
                            onClick={() => {
                              const columnTaskIds = column.items
                                .map(item => item.key || item.pod_task_id)
                                .filter(id => selectedTasks.includes(id));
                              handleLaunchAgentPipeline(columnTaskIds);
                            }}
                            sx={{ 
                              py: 0.2, 
                              px: 1.5,
                              minHeight: '26px',
                              fontSize: '0.7rem', 
                              fontWeight: 700,
                              textTransform: 'none',
                              borderRadius: '4px'
                            }}
                          >
                            Build
                          </Button>
                        )}
                      </Box>

                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      column.items.map((task, index) => {
                        const taskId = task.key || task.pod_task_id || `task-${index}`;
                        return (
                          <Draggable
                            key={taskId}
                            draggableId={taskId.toString()}
                            index={index}
                          >
                            {(provided) => (
                              <StyledTask
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                sx={{
                                  borderColor: selectedTasks.includes(taskId) ? 'primary.main' : 'divider',
                                  backgroundColor: selectedTasks.includes(taskId) ? 'rgba(0, 194, 255, 0.08)' : 'background.paper',
                                  boxShadow: selectedTasks.includes(taskId) ? '0 0 12px rgba(0, 194, 255, 0.15)' : 'none',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <Box 
                                    sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'space-between',
                                      mb: 1,
                                      pb: 1,
                                      borderBottom: '1px solid',
                                      borderColor: 'divider'
                                    }}
                                  >
                                    <Box 
                                      sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 0.5,
                                        bgcolor: selectedTasks.includes(taskId) ? 'primary.main' : 'action.hover',
                                        color: selectedTasks.includes(taskId) ? 'white' : 'text.secondary',
                                        px: 1,
                                        py: 0.2,
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleTaskSelection(taskId, e);
                                      }}
                                    >
                                      <Checkbox
                                        checked={selectedTasks.includes(taskId)}
                                        onChange={(e) => handleTaskSelection(taskId, e)}
                                        onClick={(e) => e.stopPropagation()}
                                        size="small"
                                        sx={{ 
                                          p: 0, 
                                          color: selectedTasks.includes(taskId) ? 'white' : 'inherit',
                                          '&.Mui-checked': { color: 'white' }
                                        }}
                                      />
                                      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase' }}>
                                        {selectedTasks.includes(taskId) ? 'Build Enabled' : 'Build AI'}
                                      </Typography>
                                    </Box>
                                    
                                    {task.key && (
                                      <Typography
                                        sx={{
                                          bgcolor: 'primary.main',
                                          color: 'primary.contrastText',
                                          px: 1,
                                          py: 0.2,
                                          borderRadius: 1,
                                          fontSize: '0.75rem',
                                          fontWeight: 600
                                        }}
                                      >
                                        {task.key}
                                      </Typography>
                                    )}
                                  </Box>
                                  
                                  <Button
                                    onClick={() => handleTaskClick(task)}
                                    sx={{
                                      width: '100%',
                                      textAlign: 'left',
                                      color: 'text.primary',
                                      textTransform: 'none',
                                      p: 0,
                                      display: 'block'
                                    }}
                                  >
                                    <Box className="task-content">
                                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                        {task.key ? (
                                          <FaJira size={16} color={theme.palette.primary.main} />
                                        ) : (
                                          <FaBug size={16} color={theme.palette.success.main} />
                                        )}
                                        <Typography sx={{ fontWeight: 500 }}>{task.summary || task.pod_task_name}</Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PersonIcon color="success" />
                                        <Typography variant="body2">{task.Candidate_Name}</Typography>
                                      </Box>
                                      <Typography variant="body2" sx={{ mt: 1 }}>
                                        {intl.formatMessage({ id: "dueDate" })}: {moment(task.due_date).format("MM-DD-YYYY") || "Not Set"}
                                      </Typography>
                                    </Box>
                                  </Button>
                                </Box>
                              </StyledTask>
                            )}
                          </Draggable>
                        );
                      })
                    )}
                    {provided.placeholder}
                  </StyledColumn>
                );
              }}
            </Droppable>
            ))}
          </Box>
        </DragDropContext>
        <ScrollButton
          className="scroll-button scroll-right"
          onClick={() => handleScroll("right")}
        >
          →
        </ScrollButton>

        <Slide direction="up" in={selectedTasks.length > 0} mountOnEnter unmountOnExit>
          <ActionBar>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {selectedTasks.length} task{selectedTasks.length !== 1 ? 's' : ''} selected
              </Typography>
              <Button size="small" onClick={handleClearSelection} sx={{ textTransform: 'none' }}>
                Clear selection
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <button
                className="artemy-btn"
                onClick={handleLaunchAgentPipeline}
                disabled={isLaunchingPipeline}
                style={{
                  background: isLaunchingPipeline ? 'rgba(0,194,255,0.3)' : 'linear-gradient(135deg, #00C2FF, #7B2FFF)',
                  border: 'none',
                  fontWeight: 700,
                  letterSpacing: '0.03em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: isLaunchingPipeline ? 'not-allowed' : 'pointer'
                }}
              >
                {isLaunchingPipeline ? '🔄 Launching...' : '🚀 Launch Agent Pipeline'}
              </button>
            </Box>
          </ActionBar>
        </Slide>

        {/* Pipeline Result Dialog */}
        <Dialog
          open={showPipelineResult}
          onClose={() => setShowPipelineResult(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
              color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
              backgroundImage: 'none',
              border: `1px solid ${pipelineResult?.success ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'}`,
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
            }
          }}
        >
          <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}`, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5">{pipelineResult?.success ? '✅' : '❌'}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: pipelineResult?.success ? '#10B981' : '#EF4444' }}>
                {pipelineResult?.success ? 'Pipeline Launched!' : 'Launch Failed'}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {pipelineResult?.success ? (
              <Box>
                <Typography variant="body1" sx={{ color: theme.palette.mode === 'dark' ? '#E5E7EB' : '#374151', mb: 2 }}>
                  Successfully submitted <strong>{pipelineResult.count}</strong> task{pipelineResult.count !== 1 ? 's' : ''} to the Agentic AI pipeline.
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? '#9CA3AF' : '#6B7280' }}>
                  The agents will begin processing. You can track progress in the{' '}
                  <strong
                    style={{ color: '#00C2FF', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => {
                      setShowPipelineResult(false);
                      window.location.href = '/Workflow';
                    }}
                  >
                    Build Workflow
                  </strong>{' '}
                  workspace and check Pending Approvals for results.
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="body1" sx={{ color: theme.palette.mode === 'dark' ? '#E5E7EB' : '#374151', mb: 2 }}>
                  Could not launch pipeline for {pipelineResult?.count} task{pipelineResult?.count !== 1 ? 's' : ''}.
                </Typography>
                <Typography variant="body2" sx={{ color: '#EF4444' }}>
                  {pipelineResult?.error || 'An unexpected error occurred. Please try again.'}
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </StyledBox >

      {/* Comment Drawer */}
      <StyledDrawer
        anchor="right"
        open={!!selectedTask}
        onClose={handleDrawerClose}
      >
        <Box sx={{ width: 400, p: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              {selectedTask?.key ? (
                <FaJira size={16} color={theme.palette.primary.main} />
              ) : (
                <FaBug size={16} color={theme.palette.success.main} />
              )}
              <Typography variant="subtitle1">
                {intl.formatMessage({ id: "taskDetails" })}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="h6">
                {selectedTask?.key || selectedTask?.pod_task_id} - {selectedTask?.summary || selectedTask?.pod_task_name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ fontSize: 16 }} />
                <Typography variant="body2">
                  Assignee: {selectedTask?.Candidate_Name || 'Not Assigned'}
                </Typography>
              </Box>

              <Typography variant="body2">
                {intl.formatMessage({ id: "dueDate" })}: {moment(selectedTask?.due_date).format("MM-DD-YYYY") || "Not Set"}

              </Typography>

              <Typography variant="body2">
                {intl.formatMessage({ id: "status" })}: {selectedTask?.status}
              </Typography>

              {selectedTask?.priority && (
                <Typography variant="body2">
                  {intl.formatMessage({ id: "priority" })}: {selectedTask.priority}
                </Typography>
              )}


            </Box>
          </Box>


          <Box sx={{
            maxHeight: 'calc(100vh - 300px)',
            overflowY: 'auto',
            px: 0.5,
            mb: 2
          }}>
            {renderComments()}
          </Box>

          <Box sx={{
            borderTop: 1,
            borderColor: 'divider',
            pt: 2,
            mt: 2,
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.default'
          }}>
            <TextField
              label="Add a comment"
              multiline
              rows={3}
              fullWidth
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              variant="outlined"
              size="small"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddComment}
              disabled={!newComment.trim()}
              sx={{ mt: 1, float: 'right' }}
            >
              {intl.formatMessage({ id: "addComment" })}
            </Button>
          </Box>
        </Box>
      </StyledDrawer>
    </Box >
  );
};

export default KanbanboardTalent;